import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { UserHelperService } from '@helpers/user-helper.service';
import { UserAttributesI, UserI } from '@interfaces/user.interface';
import { Observable, firstValueFrom } from 'rxjs';
import {
  GroupI,
  GroupMentionI,
  GroupParticipantsI,
  GroupStepI,
  GroupSubjectI,
  MessageGroupI,
} from '@interfaces/group.interface';
import { MessageTypeT } from '@omnitypes/message.type';
import { v4 as uuidv4 } from 'uuid';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
import { PageI } from '@interfaces/page.interface';
import { MessageStatusE, MessageTypeE, SenderTypeE } from '@enums/message.enum';
import { GroupReadStatus } from '@enums/group-read-status.enum';
import { GroupFilterI } from '@interfaces/group-filter.interface';
import { ChannelE, ChannelI } from '@interfaces/channel.interface';
import { ContentI } from '@interfaces/message.interface';
import { ContactI } from '@interfaces/contact.interface';
import { GroupStepTypeE } from '@enums/group-step-type.enum';
import { MentionI } from '@interfaces/mention.interface';

@Injectable({
  providedIn: 'root',
})
export class GroupService {
  public $toggleEmoji = new EventEmitter<boolean>();

  private baseUrl = environment.OMNISMART_API_CHAT_PERSISTENCE;

  private channelIdSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private messageGroupSelected = new BehaviorSubject<MessageGroupI | undefined>(undefined);

  public channelId$: Observable<string> = this.channelIdSubject.asObservable();

  public channelType: ChannelE = ChannelE.WHATSAPP;

  constructor(private http: HttpClient, private readonly userHelperService: UserHelperService) {}

  async setChannel(channelId: string) {
    this.channelIdSubject.next(channelId);
  }

  public setChannelType(channelType: ChannelE) {
    this.channelType = channelType;
  }

  public create(options: {
    channel: ChannelI;
    subject: string;
    participants: GroupParticipantsI[];
    photo?: string;
    onlySee?: boolean;
  }): Observable<GroupI> {
    const { channel, subject, participants, photo, onlySee } = options;

    return this.http.post<GroupI>(`${this.baseUrl}groups/`, {
      customerId: channel.customerId,
      channel: channel._id,
      subject,
      participants,
      photo,
      onlySee,
    });
  }

  public delete(groupId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}groups/${groupId}`);
  }

  public update(groupId: string, data: any): Observable<GroupI> {
    return this.http.patch<GroupI>(`${this.baseUrl}groups/${groupId}`, data);
  }

  public addParticipants(options: {
    groupId: string;
    participants: GroupParticipantsI[];
  }): Observable<GroupParticipantsI[]> {
    const { groupId, participants } = options;

    return this.http.post<GroupParticipantsI[]>(`${this.baseUrl}groups/${groupId}/add-participants`, {
      participants,
    });
  }

  public removeParticipants(options: {
    groupId: string;
    participants: GroupParticipantsI[];
  }): Observable<GroupParticipantsI[]> {
    const { groupId, participants } = options;

    return this.http.post<GroupParticipantsI[]>(`${this.baseUrl}groups/${groupId}/remove-participants`, {
      participants,
    });
  }

  setAllMessagesAsRead(groupId: string, key: any) {
    return this.http.patch<void>(`${this.baseUrl}groups/${groupId}/update-messages-status`, {
      status: MessageStatusE.READ,
    });
  }

  public async generateMessage(
    groupId: string,
    content: ContentI,
    type: MessageTypeT,
    isForwarding: boolean = false,
    audioRecorded = false,
    caption?: string,
    isVoiceMessage?: boolean,
    mentions?: MentionI[],
    scheduledTo?: Date
  ): Promise<MessageGroupI> {
    let message: MessageGroupI = this.getTemplateMessage(groupId, isForwarding);

    message.type = type as any;
    if (type === 'text' || type === 'note') {
      message.content = { text: content.message ?? content.text };
    } else {
      message.content = content as any;
    }

    message.caption = caption;

    if (isVoiceMessage !== undefined) {
      message.isVoiceMessage = isVoiceMessage;
    }

    if (mentions) {
      message.content.mentions = mentions;
    }

    if (scheduledTo) {
      message.scheduledTo = scheduledTo;
    }

    return message;
  }

  private getTemplateMessage(groupId: string, forwardedMessage: boolean = false): MessageGroupI {
    const user = this.userHelperService.getUserAttributes() as UserAttributesI;
    return {
      _id: uuidv4(),
      customerId: user['custom:customer'],
      group: { _id: groupId } as GroupI,
      sender: {
        _id: user['custom:userId'],
        name: user.name,
        avatar: user.picture,
        customerId: user['custom:customer'],
        type: SenderTypeE.AGENT,
        senderId: user['custom:userId'],
      },
      status: MessageStatusE.PENDING,
      type: MessageTypeE.TEXT,
      content: {},
      time: Date.now().toFixed(),
      channelType: this.channelType,
      forwardedMessage,
    };
  }

  setMessageSelected(message: MessageGroupI): void {
    this.messageGroupSelected.next(message);
  }

  getMessageSelected(): Observable<MessageGroupI | undefined> {
    return this.messageGroupSelected.asObservable();
  }

  public updateChatReadStatus(id: string, readStatus: GroupReadStatus): Observable<any> {
    return this.http.patch(this.baseUrl + 'whatsapp-group/update-read-status/' + id, {
      readStatus,
    });
  }

  public sendForwardMessage(groupId, message: MessageGroupI) {
    return this.sendMessage(message, groupId);
  }

  public addAgentsInGroup(groupId: string, agents: string[]): Observable<any> {
    return this.update(groupId, { agents });
  }

  public findScheduled(groupId: string, page: number): Observable<PageI<MessageGroupI>> {
    const params = new HttpParams()
      .set('group', groupId ?? '')
      .set('sort', 'desc')
      .set('limit', '50')
      .set('page', String(page));

    return this.http.get<PageI<MessageGroupI>>(`${this.baseUrl}groups/scheduled`, { params });
  }

  public updateScheduledMessage(
    messageId: string,
    jobId: string | number,
    groupId: string,
    message: MessageGroupI
  ): Observable<MessageGroupI> {
    return this.http.patch<MessageGroupI>(`${this.baseUrl}groups/scheduled/${messageId}/${jobId}`, {
      groupId,
      message,
    });
  }

  public dropScheduledMessage(messageId: string, jobId: string | number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}groups/scheduled/${messageId}/${jobId}`);
  }

  public sendMessage(message: MessageGroupI, groupId: string): Observable<MessageGroupI> {
    try {
      return this.http.post<MessageGroupI>(`${this.baseUrl}groups/${groupId}/send-message`, { message });
    } catch (error) {
      throw new Error(`GroupContainerFooterService sendMessage error: ${error}`);
    }
  }

  public deleteMessages(groupId: string, groupMessageIds: string[]): Observable<void> {
    return this.http.patch<void>(this.baseUrl + `groups/${groupId}/delete-messages`, groupMessageIds);
  }

  public updateMessage(groupId: string, messageId: string, data: Partial<MessageGroupI>): Observable<MessageGroupI> {
    return this.http.patch<MessageGroupI>(`${this.baseUrl}groups/${groupId}/update-message/${messageId}`, data);
  }

  public getAllGroups(
    page = 1,
    limit = 10,
    search = '',
    filter: GroupFilterI = {} as GroupFilterI,
    allGroups: boolean = false,
    channelType?: string
  ): Observable<PageI<GroupI>> {
    return this.http.get<PageI<GroupI>>(this.baseUrl + `groups`, {
      params: this.getFindParams(page, limit, search, filter, allGroups, channelType),
    });
  }

  public getAllGroupSubjects(
    page = 1,
    limit = 10,
    search = '',
    filter: GroupFilterI = {} as GroupFilterI,
    allGroups: boolean = false,
    channelType?: string
  ): Observable<PageI<GroupSubjectI>> {
    return this.http.get<PageI<GroupSubjectI>>(this.baseUrl + `groups/subjects`, {
      params: this.getFindParams(page, limit, search, filter, allGroups, channelType),
    });
  }

  public getAllGroupsById(
    channelId?: string,
    page = 1,
    limit = 10,
    search = '',
    filter: GroupFilterI = {} as GroupFilterI,
    allGroups: boolean = false
  ): Observable<PageI<GroupI>> {
    let params = this.getFindParams(page, limit, search, filter);
    if (allGroups) {
      params = params.set('allGroups', 'true');
    }
    return this.http.get<PageI<GroupI>>(
      this.baseUrl + `groups/find-by-channel/${channelId ?? this.channelIdSubject.value}`,
      { params }
    );
  }

  public getGroupMessages(
    groupId: string,
    page = 1,
    search = '',
    sort = 'desc',
    limit = 50,
    active = 'createdAt',
    types: MessageTypeT[] | null = null
  ): Observable<PageI<MessageGroupI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString())
      .set('types', types ? JSON.stringify(types) : '');
    return this.http.get<PageI<MessageGroupI>>(
      `${this.baseUrl}groups/${groupId}/${this.channelIdSubject.value}/messages`,
      {
        params,
      }
    );
  }

  private getFindParams(page, limit, search, filter, allGroups?: boolean, channelType?: string) {
    let httpParams = new HttpParams().set('page', page).set('limit', limit);
    if (search) {
      httpParams = httpParams.set('search', search);
    }
    if (allGroups) {
      httpParams = httpParams.set('allGroups', JSON.stringify(allGroups));
    }
    if (channelType) {
      httpParams = httpParams.set('channelType', channelType);
    }
    if (filter) {
      httpParams = httpParams.set('filter', JSON.stringify(filter));
    }
    return httpParams;
  }

  public updateParticipants(groupId: string, data: any): Observable<GroupI> {
    return this.http.patch<GroupI>(`${this.baseUrl}groups/${groupId}/update-group-participants`, data);
  }

  public findOne(groupId: string): Observable<GroupI> {
    return this.http.get<GroupI>(`${this.baseUrl}groups/${groupId}`);
  }

  public getGroupParticipants(
    groupId: string,
    page = 1,
    search = '',
    sort = 'desc',
    limit = 50,
    active = 'createdAt'
  ): Observable<PageI<GroupParticipantsI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<GroupParticipantsI>>(`${this.baseUrl}groups/${groupId}/participants`, {
      params,
    });
  }

  public async getAllGroupParticipants(group: GroupI): Promise<GroupParticipantsI[]> {
    const participants = group.lazyLoadParticipants
      ? (await firstValueFrom(this.getGroupParticipants(group._id!, 1, '', 'desc', 9999))).data
      : [];

    return (group.participants ?? [])
      .concat(participants)
      .filter((participant) => !participant.isDeleted)
      .sort((a, b) => {
        const contactA = a?.contact as ContactI;
        const contactB = b?.contact as ContactI;

        const userA = a?.user as UserI;
        const userB = b?.user as UserI;

        const nameContactA = contactA ? contactA.name : null;
        const nameContactB = contactB ? contactB.name : null;

        const nameUserA = userA ? userA.name : null;
        const nameUserB = userB ? userB.name : null;

        const nameA = (nameContactA ?? nameUserA ?? '').toLowerCase();
        const nameB = (nameContactB ?? nameUserB ?? '').toLowerCase();

        if (nameA < nameB) {
          return -1;
        }

        if (nameA > nameB) {
          return 1;
        }

        return 0;
      });
  }

  public updateParticipant(groupId: string, data: any): Observable<GroupParticipantsI> {
    return this.http.patch<GroupParticipantsI>(`${this.baseUrl}groups/${groupId}/update-group-participant`, data);
  }

  public createStepCallInitiated(groupId: string, room: string): Observable<GroupStepI> {
    return this.http.patch<GroupStepI>(`${this.baseUrl}groups/${groupId}/create-step-call-initiated`, {
      type: GroupStepTypeE.CALL_INITIATED,
      createdAt: new Date(),
      room,
    });
  }

  public createStepCallEnded(groupId: string, room: string): Observable<GroupStepI> {
    return this.http.patch<GroupStepI>(`${this.baseUrl}groups/${groupId}/create-step-call-ended`, {
      type: GroupStepTypeE.CALL_ENDED,
      createdAt: new Date(),
      room,
    });
  }

  public updateMentions(groupId: string, mentions: GroupMentionI[]): Observable<any> {
    return this.http.patch(`${this.baseUrl}groups/${groupId}/update-mentions`, {
      mentions,
    });
  }
}
